import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search, Visibility } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '../../i18n';
import AppBarSpacer from '../../components/common/AppBarSpacer';
import { ManagementTableHeaderCell } from '../../components/common/ManagementTable';
import MiniDrawer from '../../components/MiniDrawer';
import PageHeader from '../../components/common/PageHeader';
import { apiGet } from '../../utils/apiClient';
import { AUDIT_MODULES, deriveModule } from '../../utils/auditModule';
import { notifyFailure } from '../../utils/toast';

type AuditLog = {
  id: number;
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
  method: string;
  path: string;
  route: string | null;
  resource: string | null;
  action: string | null;
  statusCode: number;
  success: boolean;
  ip: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  createdAt: string;
};

const METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
const ACTIONS = [
  'login',
  'logout',
  'refresh',
  'create',
  'update',
  'delete',
  'merge',
  'import',
  'revert',
];

const methodColor = (
  method: string,
): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  if (method === 'POST') return 'success';
  if (method === 'PUT' || method === 'PATCH') return 'info';
  if (method === 'DELETE') return 'error';
  return 'default';
};

const AuditLogSetting = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [method, setMethod] = useState('');
  const [action, setAction] = useState('');
  const [module, setModule] = useState('');
  const [result, setResult] = useState<'all' | 'true' | 'false'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [detail, setDetail] = useState<AuditLog | null>(null);

  const actionLabel = (value: string | null): string => {
    if (!value) return '—';
    return ACTIONS.includes(value) ? t(`auditLog.action_${value}`) : value;
  };

  const moduleLabel = (key: string): string =>
    key ? t(`auditLog.module_${key}`) : '—';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (appliedSearch) params.set('search', appliedSearch);
      if (method) params.set('method', method);
      if (action) params.set('action', action);
      if (module) params.set('module', module);
      if (result !== 'all') params.set('success', result);

      const res = await apiGet<{
        data: AuditLog[];
        metadata: { total: number };
      }>(`/audit/logs?${params.toString()}`);
      setLogs(res.data);
      setTotal(res.metadata.total);
    } catch {
      notifyFailure();
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedSearch, method, action, module, result]);

  useEffect(() => {
    void load();
  }, [load]);

  const applySearch = () => {
    setPage(0);
    setAppliedSearch(search);
  };

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title={t('auditLog.title')}
            subtitle={t('auditLog.subtitle')}
          />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}
            flexWrap="wrap"
          >
            <TextField
              size="small"
              placeholder={t('auditLog.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applySearch();
              }}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="audit-method">{t('auditLog.method')}</InputLabel>
              <Select
                labelId="audit-method"
                label={t('auditLog.method')}
                value={method}
                onChange={(event) => {
                  setPage(0);
                  setMethod(event.target.value);
                }}
              >
                <MenuItem value="">{t('auditLog.allMethods')}</MenuItem>
                {METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="audit-action">{t('auditLog.action')}</InputLabel>
              <Select
                labelId="audit-action"
                label={t('auditLog.action')}
                value={action}
                onChange={(event) => {
                  setPage(0);
                  setAction(event.target.value);
                }}
              >
                <MenuItem value="">{t('auditLog.allActions')}</MenuItem>
                {ACTIONS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {actionLabel(a)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="audit-module">{t('auditLog.module')}</InputLabel>
              <Select
                labelId="audit-module"
                label={t('auditLog.module')}
                value={module}
                onChange={(event) => {
                  setPage(0);
                  setModule(event.target.value);
                }}
              >
                <MenuItem value="">{t('auditLog.allModules')}</MenuItem>
                {AUDIT_MODULES.map((key) => (
                  <MenuItem key={key} value={key}>
                    {moduleLabel(key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="audit-result">{t('auditLog.result')}</InputLabel>
              <Select
                labelId="audit-result"
                label={t('auditLog.result')}
                value={result}
                onChange={(event) => {
                  setPage(0);
                  setResult(event.target.value as 'all' | 'true' | 'false');
                }}
              >
                <MenuItem value="all">{t('auditLog.resultAll')}</MenuItem>
                <MenuItem value="true">{t('auditLog.resultSuccess')}</MenuItem>
                <MenuItem value="false">{t('auditLog.resultFailed')}</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={applySearch}>
              {t('auditLog.apply')}
            </Button>
          </Stack>

          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colWhen')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colUser')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colModule')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colAction')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colMethod')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colPath')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell>
                      {t('auditLog.colStatus')}
                    </ManagementTableHeaderCell>
                    <ManagementTableHeaderCell align="right">
                      {t('auditLog.colActions')}
                    </ManagementTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3, textAlign: 'center' }}
                        >
                          {t('auditLog.empty')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(log.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {log.userName || t('auditLog.systemUser')}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {moduleLabel(deriveModule(log.path))}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={actionLabel(log.action)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={methodColor(log.method)}
                            label={log.method}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 360,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={log.path}
                        >
                          {log.path}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={log.success ? 'success' : 'error'}
                            label={log.statusCode}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => setDetail(log)}
                          >
                            {t('auditLog.viewDetail')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </div>
      </main>

      <Dialog
        open={detail !== null}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('auditLog.detailTitle', { id: detail?.id ?? '' })}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={1}>
              <Typography variant="body2">
                <b>{t('auditLog.colWhen')}:</b>{' '}
                {new Date(detail.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <b>{t('auditLog.colUser')}:</b>{' '}
                {detail.userName || t('auditLog.systemUser')}
                {detail.userEmail ? ` · ${detail.userEmail}` : ''}
                {detail.userId ? ` (#${detail.userId})` : ''}
              </Typography>
              <Typography variant="body2">
                <b>{t('auditLog.colAction')}:</b> {actionLabel(detail.action)} ·{' '}
                {detail.method} {detail.statusCode}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <b>{t('auditLog.colPath')}:</b> {detail.path}
              </Typography>
              {detail.route && (
                <Typography variant="body2">
                  <b>{t('auditLog.route')}:</b> {detail.route}
                </Typography>
              )}
              {detail.ip && (
                <Typography variant="body2">
                  <b>{t('auditLog.colIp')}:</b> {detail.ip}
                </Typography>
              )}
              <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                {t('auditLog.payload')}
              </Typography>
              {detail.payload ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 360,
                  }}
                >
                  {JSON.stringify(detail.payload, null, 2)}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('auditLog.noPayload')}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>{t('auditLog.close')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AuditLogSetting;
